import { useTranslation } from 'react-i18next'

/** The prototype's `.qty-ctrl`: − / value / +. With `editable` it is the
 * `.qty-editable` variant (MS-820): the value is typeable (digits only, shown
 * with thousands separators, 96px wide), and clearing the field to type a new
 * number reads as 0 rather than an error — the action the value feeds is what
 * enforces a minimum (the cart's Purchase, for one). `stepTo` replaces the
 * plain ±`step` for a stepper that snaps (the projected-consumption ladder).
 * `tone="info"` is the simulation's blue `.sim-input .qty-ctrl`. Focus stays
 * in the input while typing: React updates it in place, unlike the
 * prototype's re-render.
 *
 * MS-822: `value` may be null — an empty field showing `placeholder` (the
 * User Limits default before any figure is set, "Set a limit"); a step from
 * empty starts at 0. `disabled` greys the whole control to opacity .4 and
 * makes it inert, still showing its value (the default limit while it is
 * switched off, `renderDefault`). */
export function QtyStepper({
  value,
  onChange,
  min = 1,
  step = 1,
  editable = false,
  stepTo,
  tone = 'default',
  label,
  placeholder,
  disabled = false,
}: Readonly<{
  value: number | null
  onChange: (next: number) => void
  min?: number
  step?: number
  editable?: boolean
  stepTo?: (current: number, direction: 1 | -1) => number
  tone?: 'default' | 'info'
  /** Accessible name of the value input. */
  label?: string
  placeholder?: string
  disabled?: boolean
}>) {
  const { i18n } = useTranslation()
  const current = value ?? 0
  const next = (direction: 1 | -1) =>
    Math.max(min, stepTo ? stepTo(current, direction) : current + direction * step)
  const frame = tone === 'info' ? 'border-[var(--info-border)] bg-[var(--bg-card)]' : 'border-[var(--border)]'
  const button = `w-[30px] h-[30px] text-[16px] bg-[var(--bg-card)] hover:bg-[rgba(128,128,128,.12)] disabled:pointer-events-none ${
    tone === 'info' ? 'text-[var(--info)]' : 'text-[var(--text-primary)]'
  }`
  return (
    <div
      className={`inline-flex items-center border rounded-lg overflow-hidden transition-opacity ${frame} ${disabled ? 'opacity-40' : ''}`}
    >
      <button type="button" disabled={disabled} onClick={() => onChange(next(-1))} className={button}>
        −
      </button>
      <input
        readOnly={!editable}
        disabled={disabled}
        inputMode={editable ? 'numeric' : undefined}
        aria-label={label}
        placeholder={placeholder}
        value={value === null ? '' : value.toLocaleString(i18n.language)}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '')
          onChange(digits === '' ? 0 : Number(digits))
        }}
        className={`${editable ? 'w-24' : 'w-14'} h-[30px] text-center text-[13px] border-x border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-primary)]`}
      />
      <button type="button" disabled={disabled} onClick={() => onChange(next(1))} className={button}>
        +
      </button>
    </div>
  )
}
